<template>
  <section class="live-trades" :class="{ 'live-trades--dark': $vuetify.theme.dark }">
    <div class="live-trades__toolbar">
      <v-text-field
        v-model="search"
        class="live-trades__control live-trades__search"
        prepend-inner-icon="mdi-magnify"
        placeholder="Search here..."
        solo flat dense hide-details clearable
      />

      <v-select
        v-model="account"
        :items="accountOptions"
        class="live-trades__control"
        append-icon="mdi-chevron-down"
        solo flat dense hide-details
      />

      <v-dialog ref="dateDialog" v-model="modal" :return-value.sync="dates" width="310px">
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :value="dateRangeText"
            class="live-trades__control live-trades__date"
            prepend-inner-icon="mdi-calendar-blank-outline"
            append-icon="mdi-chevron-down"
            readonly solo flat dense hide-details
            v-bind="attrs"
            v-on="on"
          />
        </template>
        <v-date-picker v-model="dates" scrollable range>
          <v-spacer />
          <v-btn text color="#ff4659" @click="clearDates">Reset</v-btn>
          <v-btn text color="primary" @click="$refs.dateDialog.save(dates)">Apply</v-btn>
        </v-date-picker>
      </v-dialog>

      <div class="live-trades__totals">
        <div v-for="stat in summaryStats" :key="stat.label" class="live-trades__total">
          <span>{{ stat.label }}</span>
          <strong :class="`is-${stat.tone}`">{{ stat.value }}</strong>
        </div>
      </div>
    </div>

    <v-card flat class="live-trades__table-card">
      <v-data-table
        :headers="headers"
        :items="filteredData"
        :items-per-page="5"
        :footer-props="footerProps"
        :loading="loading"
        class="live-trades__table"
        loading-text="Loading live trades..."
      >
        <template v-slot:[`item.serial`]="{ index }">{{ index + 1 }}</template>
        <template v-slot:[`item.TimeCreate`]="{ item }">{{ formatDateTime(item.TimeCreate) }}</template>
        <template v-slot:[`item.Volume`]="{ item }">{{ formatNumber(lotValue(item), 2) }}</template>
        <template v-slot:[`item.PriceOpen`]="{ item }">{{ formatNumber(item.PriceOpen, 5) }}</template>
        <template v-slot:[`item.PriceSL`]="{ item }">{{ slTpValue(item) }}</template>
        <template v-slot:[`item.PriceCurrent`]="{ item }">{{ formatCurrency(item.PriceCurrent) }}</template>
        <template v-slot:[`item.Equity`]="{ item }">{{ formatCurrency(item.Equity) }}</template>
        <template v-slot:[`item.Profit`]="{ item }">
          <strong :class="valueTone(item.Profit)">{{ formatCurrency(item.Profit) }}</strong>
        </template>
        <template v-slot:no-data>
          <div class="live-trades__empty">
            <v-icon size="46">mdi-briefcase-outline</v-icon>
            <span>No data available</span>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <MiddleBar />
    <LiveAccounts />
  </section>
</template>

<script>
import { mapActions, mapState } from "vuex";
import { TRADING__ACTIONS } from "@/store/action-types";
import LiveAccounts from "./LiveAccounts.vue";
import MiddleBar from "./MiddleBar.vue";

export default {
  name: "LiveTradesTab",
  components: { MiddleBar, LiveAccounts },

  data() {
    return {
      modal: false,
      search: "",
      account: "All Accounts",
      dates: [],
      loading: false,
      footerProps: {
        "items-per-page-options": [5],
        "items-per-page-text": "Rows per page:",
        "show-current-page": true,
        "show-first-last-page": false,
      },
      headers: [
        { text: "Sl", value: "serial", sortable: false, width: 52 },
        { text: "Date & Time", value: "TimeCreate", width: 150 },
        { text: "Acc. No.", value: "trading_account_no", width: 120 },
        { text: "Ticket", value: "Position", width: 110 },
        { text: "Acc. Type", value: "tradingaccounttypes.account_type_display", width: 130 },
        { text: "Lots", value: "Volume", width: 70 },
        { text: "Symbol", value: "Symbol", width: 95 },
        { text: "Open Price($)", value: "PriceOpen", width: 112 },
        { text: "SL-TP", value: "PriceSL", sortable: false, width: 145 },
        { text: "Running($)", value: "PriceCurrent", width: 105 },
        { text: "Equity($)", value: "Equity", width: 100 },
        { text: "Profit($)", value: "Profit", width: 100 },
      ],
    };
  },

  computed: {
    ...mapState("trading", ["allLiveTrades", "allLiveAccounts", "accountSummary"]),

    sourceTrades() {
      return Array.isArray(this.allLiveTrades) ? this.allLiveTrades : [];
    },

    accountOptions() {
      const tradeAccounts = this.sourceTrades.map((item) => item.trading_account_no);
      const liveAccounts = (this.allLiveAccounts || []).map((item) => item.trading_account_no);
      return ["All Accounts", ...new Set([...tradeAccounts, ...liveAccounts].filter(Boolean))];
    },

    filteredData() {
      let rows = this.sourceTrades;

      if (this.account && this.account !== "All Accounts") {
        rows = rows.filter((item) => String(item.trading_account_no) === String(this.account));
      }

      if (this.dates.length === 2) {
        const boundaries = this.dates.slice().sort();
        const start = new Date(`${boundaries[0]}T00:00:00`);
        const end = new Date(`${boundaries[1]}T23:59:59`);
        rows = rows.filter((item) => {
          const tradeDate = this.parseDate(item.TimeCreate);
          return tradeDate && tradeDate >= start && tradeDate <= end;
        });
      }

      const query = String(this.search || "").trim().toLowerCase();
      if (query) {
        rows = rows.filter((item) => [
          item.Position,
          item.Symbol,
          item.trading_account_no,
          item.tradingaccounttypes?.account_type_display,
        ].some((value) => String(value || "").toLowerCase().includes(query)));
      }

      return rows;
    },

    dateRangeText() {
      if (this.dates.length === 2) return this.dates.slice().sort().join(" - ");
      return [this.getSevenDaysAgoDate(), this.getCurrentDate()].join(" - ");
    },

    usesFullSummary() {
      return this.account === "All Accounts" && this.dates.length !== 2 && !this.search;
    },

    totalEquity() {
      if (this.usesFullSummary && this.accountSummary?.liveTrades?.equity !== undefined) {
        return this.numberValue(this.accountSummary.liveTrades.equity);
      }
      const accountEquities = new Map();
      this.filteredData.forEach((item) => accountEquities.set(item.trading_account_no, this.numberValue(item.Equity)));
      return [...accountEquities.values()].reduce((sum, value) => sum + value, 0);
    },

    totalLots() {
      if (this.usesFullSummary && this.accountSummary?.liveTrades?.totalLot !== undefined) {
        return this.numberValue(this.accountSummary.liveTrades.totalLot);
      }
      return this.filteredData.reduce((sum, item) => sum + this.lotValue(item), 0);
    },

    totalProfit() {
      if (this.usesFullSummary && this.accountSummary?.liveTrades?.profitLoss !== undefined) {
        return this.numberValue(this.accountSummary.liveTrades.profitLoss);
      }
      return this.filteredData.reduce((sum, item) => sum + this.numberValue(item.Profit), 0);
    },

    summaryStats() {
      return [
        { label: "Total Equity", value: this.formatCurrency(this.totalEquity), tone: this.totalEquity >= 0 ? "neutral" : "loss" },
        { label: "Total Lot", value: this.formatNumber(this.totalLots, 2), tone: "neutral" },
        { label: "Profit / Loss", value: this.formatCurrency(this.totalProfit), tone: this.totalProfit > 0 ? "profit" : "loss" },
      ];
    },
  },

  methods: {
    ...mapActions("trading", [TRADING__ACTIONS.ALLTRADEDETAILS]),

    async fetchTrades() {
      this.loading = true;
      try {
        await this[TRADING__ACTIONS.ALLTRADEDETAILS]();
      } finally {
        this.loading = false;
      }
    },

    numberValue(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },

    lotValue(item) {
      return this.numberValue(item.Volume) / 10000;
    },

    formatNumber(value, digits = 2) {
      return this.numberValue(value).toLocaleString("en-US", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    },

    formatCurrency(value) {
      const number = this.numberValue(value);
      return `${number < 0 ? "-" : ""}$${Math.abs(number).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },

    parseDate(value) {
      if (!value) return null;
      const parsed = new Date(String(value).replace(" ", "T"));
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    },

    formatDateTime(value) {
      const date = this.parseDate(value);
      if (!date) return "—";
      const datePart = date.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
      const timePart = date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
      return `${datePart} ${timePart}`;
    },

    slTpValue(item) {
      const stopLoss = this.numberValue(item.PriceSL);
      const takeProfit = this.numberValue(item.PriceTP);
      if (!stopLoss && !takeProfit) return "—";
      return `${stopLoss ? this.formatNumber(stopLoss, 5) : "—"} / ${takeProfit ? this.formatNumber(takeProfit, 5) : "—"}`;
    },

    valueTone(value) {
      if (this.numberValue(value) > 0) return "is-profit";
      if (this.numberValue(value) < 0) return "is-loss";
      return "is-neutral";
    },

    getCurrentDate() {
      return new Date().toISOString().split("T")[0];
    },

    getSevenDaysAgoDate() {
      const date = new Date();
      date.setDate(date.getDate() - 7);
      return date.toISOString().split("T")[0];
    },

    clearDates() {
      this.dates = [];
      this.modal = false;
    },
  },

  mounted() {
    this.fetchTrades();
  },
};
</script>

<style scoped>
.live-trades {
  --panel: #fff;
  --field: #fff;
  --row: #fbfcff;
  --border: #e0e3e7;
  --text: #15181d;
  --muted: #626973;
  --profit: #05b96f;
  --loss: #ff3448;
  color: var(--text);
}
.live-trades--dark {
  --panel: #07111b;
  --field: #08131e;
  --row: #101923;
  --border: #293026;
  --text: #f4f7ff;
  --muted: #aeb6c0;
  --profit: #15dc8b;
  --loss: #ff5060;
}
.live-trades__toolbar {
  display: grid;
  grid-template-columns: minmax(230px, 1.1fr) minmax(170px, .72fr) minmax(275px, 1.35fr) minmax(380px, 1.65fr);
  gap: 10px;
  margin-bottom: 12px;
}
.live-trades__control ::v-deep .v-input__slot {
  min-height: 48px !important;
  border: 1px solid var(--border);
  border-radius: 7px !important;
  background: var(--field) !important;
  box-shadow: none !important;
}
.live-trades__control ::v-deep input,
.live-trades__control ::v-deep .v-select__selection { color: var(--text) !important; font-size: 12px; font-weight: 550; }
.live-trades__control ::v-deep .v-icon { color: #d99a00 !important; }
.live-trades__totals {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 7px;
  background: var(--panel);
}
.live-trades__total { display: flex; padding: 9px 18px; flex-direction: column; justify-content: center; border-left: 1px solid var(--border); }
.live-trades__total:first-child { border-left: 0; }
.live-trades__total span { margin-bottom: 2px; color: var(--muted); font-size: 10px; }
.live-trades__total strong { color: var(--text); font-size: 18px; line-height: 1.2; white-space: nowrap; }
.is-profit { color: var(--profit) !important; }
.is-loss { color: var(--loss) !important; }
.is-neutral { color: var(--text) !important; }
.live-trades__table-card {
  overflow: hidden;
  margin-bottom: 14px;
  border: 1px solid var(--border);
  border-radius: 8px !important;
  background: var(--panel) !important;
}
.live-trades__table { background: transparent !important; color: var(--text) !important; }
.live-trades__table ::v-deep .v-data-table__wrapper { overflow-x: auto; }
.live-trades__table ::v-deep table { min-width: 1275px; }
.live-trades__table ::v-deep th {
  height: 40px;
  padding: 0 11px !important;
  border-color: var(--border) !important;
  background: var(--panel) !important;
  color: var(--muted) !important;
  font-size: 10px !important;
  font-weight: 600 !important;
  white-space: nowrap;
}
.live-trades__table ::v-deep td {
  height: 42px;
  padding: 0 11px !important;
  border-color: var(--border) !important;
  color: var(--text) !important;
  font-size: 10.5px !important;
  white-space: nowrap;
}
.live-trades__table ::v-deep tbody tr:nth-child(odd) { background: var(--row); }
.live-trades__table ::v-deep tbody tr:hover { background: rgba(217, 154, 0, .06) !important; }
.live-trades__table ::v-deep .v-data-footer { min-height: 50px; border-color: var(--border) !important; color: var(--muted); font-size: 11px; }
.live-trades__table ::v-deep .v-data-footer .v-icon,
.live-trades__table ::v-deep .v-select__selection { color: var(--text) !important; }
.live-trades__empty { display: flex; min-height: 84px; align-items: center; justify-content: center; gap: 14px; color: var(--text); font-size: 12px; font-weight: 600; }
.live-trades__empty .v-icon { color: #d99a00; filter: drop-shadow(0 6px 7px rgba(191, 137, 0, .2)); }
.live-trades--dark .live-trades__empty .v-icon { color: #ffc400; }

@media (max-width: 1400px) {
  .live-trades__toolbar { grid-template-columns: minmax(220px, 1fr) minmax(170px, .7fr) minmax(250px, 1.1fr); }
  .live-trades__totals { grid-column: 1 / -1; }
}
@media (max-width: 850px) {
  .live-trades__toolbar { grid-template-columns: 1fr 1fr; }
  .live-trades__search, .live-trades__totals { grid-column: 1 / -1; }
}
@media (max-width: 600px) {
  .live-trades__toolbar { grid-template-columns: 1fr; }
  .live-trades__search, .live-trades__totals { grid-column: auto; }
  .live-trades__totals { grid-template-columns: 1fr; }
  .live-trades__total { min-height: 58px; border-top: 1px solid var(--border); border-left: 0; }
  .live-trades__total:first-child { border-top: 0; }
}
</style>
